import { BaseOption } from '@/hooks/useTypeToOptions';
import { getPageData } from '@/utils/getPageData';
import { InfiniteData } from '@tanstack/react-query';
import {
  GetApPayeesResponse,
  GetDeptsResponse,
  getGetApPayeesQueryKey,
  getGetDeptsQueryKey,
  getGetOfficeMembersQueryKey,
  GetOfficeMembersResponse,
} from 'ap-openapi';
import { AxiosResponse } from 'axios';
import { useCallback } from 'react';
import { useGetAllCacheDataWithQueryKey } from '../useGetAllCacheDataWithQueryKey';

// ============================================================================
// Types
// ============================================================================

/** Normalized cache item for lookups */
export type CacheItem = {
  id?: string;
  value?: string;
  name?: string;
  display_name?: string;
};

type ApPayeesData = InfiniteData<
  AxiosResponse<GetApPayeesResponse, unknown>,
  unknown
>;
type DeptsData = InfiniteData<
  AxiosResponse<GetDeptsResponse, unknown>,
  unknown
>;
type MembersData = InfiniteData<
  AxiosResponse<GetOfficeMembersResponse, unknown>,
  unknown
>;

// ============================================================================
// Utilities
// ============================================================================

/**
 * Convert BaseOption[] to CacheItem[] for lookups
 * @param useValueAsId - Use 'value' key instead of 'id' for matching
 */
export const toItems = (
  options: BaseOption[] = [],
  useValueAsId = false
): CacheItem[] =>
  options
    .filter(Boolean)
    .map((opt) =>
      useValueAsId
        ? { value: String(opt.value), name: String(opt.label) }
        : { id: String(opt.value), name: String(opt.label) }
    );

/**
 * Find single display name by id from cache data
 */
export const findOne = <T>(
  data: Array<[unknown, T | undefined]>,
  extract: (data: T) => CacheItem[],
  targetId: string,
  extraItems: CacheItem[] = [],
  getName: (item: CacheItem) => string | undefined = (i) =>
    i.name ?? i.display_name
): string | undefined => {
  const matches = (item: CacheItem) =>
    item.id === targetId || item.value === targetId;

  // Check extra items first (typically from props, smaller set)
  const fromExtra = extraItems.find(matches);
  if (fromExtra) return getName(fromExtra);

  // Search through cache data
  for (const [, cacheData] of data) {
    if (!cacheData) continue;
    const found = extract(cacheData).find(matches);
    if (found) return getName(found);
  }

  return undefined;
};

/**
 * Find multiple display names by ids from cache data
 */
export const findMany = <T>(
  data: Array<[unknown, T | undefined]>,
  extract: (data: T) => CacheItem[],
  targetIds: string[],
  extraItems: CacheItem[] = [],
  getName: (item: CacheItem) => string | undefined = (i) =>
    i.name ?? i.display_name
): Map<string, string> => {
  const resultMap = new Map<string, string>();

  const addToMap = (item: CacheItem) => {
    const id = item.id ?? item.value;
    const name = getName(item);
    if (id && name && !resultMap.has(id)) {
      resultMap.set(id, name);
    }
  };

  // Add from extra items first
  extraItems.forEach(addToMap);

  // Add from cache data
  for (const [, cacheData] of data) {
    if (cacheData) extract(cacheData).forEach(addToMap);
  }

  return resultMap;
};

// ============================================================================
// Query Keys
// ============================================================================

export const QUERY_KEYS = {
  apPayee: getGetApPayeesQueryKey()[0],
  dept: getGetDeptsQueryKey()[0],
  member: getGetOfficeMembersQueryKey()[0],
} as const;

// ============================================================================
// Extractors
// ============================================================================

export const extractApPayees = (data: ApPayeesData): CacheItem[] =>
  getPageData(data, 'ap_payees').map((item) => ({
    id: item.id,
    name: item.name,
  }));

export const extractDepts = (data: DeptsData): CacheItem[] =>
  getPageData(data, 'depts').map((item) => ({
    id: item.id,
    display_name: item.display_name,
  }));

export const extractMembers = (data: MembersData): CacheItem[] =>
  getPageData(data, 'office_members').map((item) => ({
    id: item.id,
    name: item.name,
  }));

// ============================================================================
// Base Hook
// ============================================================================

export type UseBaseValueRetrievalReturn = {
  getApPayeeValue: (id: string, options?: BaseOption[]) => string;
  getDeptValues: (ids: string[], options?: BaseOption[]) => string[];
  getOfficeMemberValues: (ids: string[], options?: BaseOption[]) => string[];
  getAllQueriesData: <T = unknown>(
    queryKey: unknown[]
  ) => Array<[unknown, T | undefined]>;
};

/**
 * Base hook for value retrieval from React Query cache
 * No prefetch side effects - use for non-invoice pages or when you need control
 *
 * @example
 * const { getApPayeeValue, getDeptValues } = useBaseValueRetrieval();
 * const payeeName = getApPayeeValue(payeeId, fallbackOptions);
 */
export const useBaseValueRetrieval = (): UseBaseValueRetrievalReturn => {
  const { getAllQueriesData } = useGetAllCacheDataWithQueryKey();

  const getApPayeeValue = useCallback(
    (id: string, options?: BaseOption[]): string => {
      if (!id) return '';
      const data = getAllQueriesData<ApPayeesData>([QUERY_KEYS.apPayee]);
      return findOne(data, extractApPayees, id, toItems(options)) ?? id;
    },
    [getAllQueriesData]
  );

  const getDeptValues = useCallback(
    (ids: string[], options?: BaseOption[]): string[] => {
      if (!ids.length) return [];
      const data = getAllQueriesData<DeptsData>([QUERY_KEYS.dept]);
      const map = findMany(
        data,
        extractDepts,
        ids,
        toItems(options),
        (i) => i.display_name
      );
      return ids.map((id) => map.get(id) ?? id);
    },
    [getAllQueriesData]
  );

  const getOfficeMemberValues = useCallback(
    (ids: string[], options?: BaseOption[]): string[] => {
      if (!ids.length) return [];
      const data = getAllQueriesData<MembersData>([QUERY_KEYS.member]);
      const map = findMany(data, extractMembers, ids, toItems(options));
      return ids.map((id) => map.get(id) ?? id);
    },
    [getAllQueriesData]
  );

  return {
    getApPayeeValue,
    getDeptValues,
    getOfficeMemberValues,
    getAllQueriesData,
  };
};

// ============================================================================
// Generic Hook Factory
// ============================================================================

export type ValueRetrievalConfig<T> = {
  queryKey: unknown[];
  extract: (data: T) => CacheItem[];
  getName?: (item: CacheItem) => string | undefined;
  useValueAsId?: boolean;
};

/**
 * Factory hook for custom value retrieval
 *
 * @example
 * const { getValue, getValues } = useGenericValueRetrieval({
 *   queryKey: ['my-data'],
 *   extract: (data) => data.items.map(i => ({ id: i.id, name: i.label })),
 * });
 */
export const useGenericValueRetrieval = <T>(
  config: ValueRetrievalConfig<T>
) => {
  const { getAllQueriesData } = useGetAllCacheDataWithQueryKey();
  const { queryKey, extract, getName, useValueAsId } = config;

  const getValue = useCallback(
    (id: string, options?: BaseOption[]): string => {
      if (!id) return '';
      const data = getAllQueriesData<T>(queryKey);
      return (
        findOne(data, extract, id, toItems(options, useValueAsId), getName) ??
        id
      );
    },
    [getAllQueriesData, queryKey, extract, getName, useValueAsId]
  );

  const getValues = useCallback(
    (ids: string[], options?: BaseOption[]): string[] => {
      if (!ids.length) return [];
      const data = getAllQueriesData<T>(queryKey);
      const map = findMany(
        data,
        extract,
        ids,
        toItems(options, useValueAsId),
        getName
      );
      return ids.map((id) => map.get(id) ?? id);
    },
    [getAllQueriesData, queryKey, extract, getName, useValueAsId]
  );

  return { getValue, getValues };
};
